// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import QtyField from "@west/App/Components/Qty/QtyField";
import { CustomAppState } from "@west/App/Store/Reducers";
import Tooltip, { TooltipPresentationProps } from "@west/MobiusOverrides/Tooltip";
import AlertWarning from "@west/XDL/Icons/AlertWarning";
import { xClassNames } from "@west/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
    // XNG-Changes
    showTooltip?: boolean;
    formId?: string;
    isMiniCart?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    hideLabelTextField?: TextFieldPresentationProps;
    alertTooltip?: TooltipPresentationProps;
    alertIcon?: IconPresentationProps;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    editableQuantityTextField: {
        labelProps: {
            variant: "legend",
            css: css`
                font-size: 10px;
                text-transform: uppercase;
                font-weight: 600;
            `,
        },
    },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
    // XNG-Changes
    formId,
    isMiniCart,
}) => {
    const [styles] = React.useState(() => mergeToNew(cartLineQuantityStyles, extendedStyles));

    // XNG-Change
    const { isTabletScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    }));

    const localOnQtyOrderedChange = (value: string, onError?: () => void) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            onError?.();
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );

    // qty less than
    const valueAsNumber = value ? +value : 0;
    const isInsufficientInventory = cartLine.qtyOnHand < valueAsNumber && cartLine.hasInsufficientInventory;
    const tooltip = isInsufficientInventory ? (
        <Tooltip
            text={siteMessage(
                "Custom_Cart_Insufficient_Inventory",
                "Product cannot be purchased at this time",
            ).toString()}
            triggerComponent={<Icon {...styles.alertIcon} src={AlertWarning} />}
            {...(isMiniCart && { xAppendTo: "parent" })}
            {...styles.alertTooltip}
        />
    ) : null;

    // XNG-Changes
    const xBlurHandler = (event: any) => {
        const value = event?.currentTarget?.value;
        if (!value) {
            event.currentTarget.value = cartLine.qtyOrdered;
            changeHandler(event);
            return;
        }
        blurHandler();
    };

    if (editable && !isOutOfStock(cartLine)) {
        return (
            <>
                <QtyField
                    label={label || translate("Qty")}
                    value={value}
                    disabled={
                        !cart.canModifyOrder || cartLine.isPromotionItem || cart.type === "Job" || isLoading[cart.id]
                    }
                    onChange={changeHandler}
                    onKeyDown={keyDownHandler}
                    onBlur={xBlurHandler}
                    onClick={event => event.currentTarget.focus()}
                    className={xClassNames.append(isInsufficientInventory && "danger")}
                    extendedStyles={styles.editableQuantityTextField}
                    data-test-selector="cartline_qty"
                    // XNG-Changes
                    formId={formId}
                />
                {tooltip}
            </>
        );
    }

    return (
        <>
            <QtyField
                label={label || translate("Qty")}
                value={cartLine.qtyOrdered!}
                disabled={true}
                data-test-selector="cartline_qty"
                extendedStyles={styles.editableQuantityTextField}
                // XNG-Changes
                formId={formId}
            />
            {tooltip}
        </>
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
